import { createAction, createReducer } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { apiCallBegan } from 'store/apiCalls'
import { gameIdSelector } from 'store/user/selectors'
import verificationMarketing from 'utils/verificationMarketBoard'

// Map new product types to API field names (API still uses old names)
const PRODUCT_TYPE_TO_API_KEY = {
  primo: 'h_basic',
  duo: 'h_plus',
  ultimo: 'h_ultra',
  nuovo: 'next_gen'
}

// Map new product types to old API marketing_power keys (fallback)
const OLD_MARKETING_POWER_KEYS = {
  primo: 'h-basic',
  duo: 'h-plus',
  ultimo: 'h-ultra',
  nuovo: 'next-gen'
}

// ACTIONS
const marketingBoardRequested = createAction('marketingBoard/requested')
const marketingBoardRequestFailed = createAction('marketingBoard/requestFailed')
const marketingBoardReceived = createAction('marketingBoard/received')

const marketingBoardSelectMarket = createAction('marketingBoard/selectMarket')
const marketingBoardSelectProductPoints = createAction('marketingBoard/selectProductPoints')

const marketingBoardPayCostsRequested = createAction('marketingBoard/payCostsRequested')
const marketingBoardPayCostsReceived = createAction('marketingBoard/payCostsReceived')
const marketingBoardPayCostsRequestFailed = createAction('marketingBoard/payCostsFailed')

const showAlertMarketingBoard = createAction('marketingBoard/showAlert')

const descriptionToAlertReceived = createAction('marketingBoard/descriptionToAlert')

// REDUCER
export const initialState = {
  data: [],
  loading: false,
  payCostsLoading: false,
  success: false,
  payCostsSuccess: false,
  selectedMarket: null,
  showAlertMarketingBoard: false,
  descriptionToAlert: ''
}

const marketingBoardListReducer = createReducer(initialState, {
  // Load marketing board
  [marketingBoardRequested.type]: (state) => {
    state.loading = true
  },
  [marketingBoardReceived.type]: (state, action) => {
    state.loading = false
    state.success = true
    state.data = action.payload.data
    state.selectedMarket = action.payload.data.length > 0 ? action.payload.data[0].market_id : null
  },
  [marketingBoardRequestFailed.type]: (state, action) => {
    state.loading = false
    state.success = false
    state.showAlertMarketingBoard = false
    toast.error(action.payload.message)
  },
  [marketingBoardSelectMarket.type]: (state, action) => {
    state.selectedMarket = action.payload
  },
  [marketingBoardSelectProductPoints.type]: (state, action) => {
    const { selectedMarket } = state

    // Update marketing board status
    const marketIndex = state.data.findIndex((item) => item.market_id === selectedMarket)
    const market = state.data[marketIndex]

    // Map new product type to API field names
    const apiKey = PRODUCT_TYPE_TO_API_KEY[action.payload.productType]
    const productType = action.payload.productType
    const marketProductName = `${apiKey}_communication_points`

    // Determine which key format marketing_power uses (new names or old kebab-case)
    const marketingPowerKey = market.marketing_power?.[productType] !== undefined
      ? productType
      : OLD_MARKETING_POWER_KEYS[productType]

    // Dynamically update marketing power
    const currentPoints = market[marketProductName] || 0
    const pointDifference = action.payload.points - currentPoints
    const currentMarketingPower = market.marketing_power?.[marketingPowerKey] || 0
    const newMarketingPowerValue =
      currentMarketingPower + (pointDifference === 0 ? -action.payload.points : pointDifference)
    const newCommunicationPoints = pointDifference === 0 ? 0 : action.payload.points

    // Create new market object with updated values
    const updatedMarket = {
      ...market,
      [marketProductName]: newCommunicationPoints,
      marketing_power: {
        ...market.marketing_power,
        [marketingPowerKey]: newMarketingPowerValue
      }
    }

    // Create new data array to ensure React detects the change
    state.data = [
      ...state.data.slice(0, marketIndex),
      updatedMarket,
      ...state.data.slice(marketIndex + 1)
    ]
  },
  // Pay costs
  [marketingBoardPayCostsRequested.type]: (state) => {
    state.payCostsLoading = true
    state.payCostsSuccess = false
  },
  [marketingBoardPayCostsReceived.type]: (state, action) => {
    state.payCostsLoading = false
    state.payCostsSuccess = true
    state.data = action.payload.data
    state.selectedMarket = action.payload.data.length > 0 ? action.payload.data[0].market_id : null
  },
  [marketingBoardPayCostsRequestFailed.type]: (state, action) => {
    state.payCostsLoading = false
    state.payCostsSuccess = false
    toast.error(action.payload.message)
  },
  [showAlertMarketingBoard.type]: (state, action) => {
    state.showAlertMarketingBoard = action.payload
  },
  [descriptionToAlertReceived.type]: (state, action) => {
    state.descriptionToAlert = action.payload
  }
})

export default marketingBoardListReducer

// PUBLIC ACTIONS
export const loadMarketingBoard = () => (dispatch, getState) => {
  const id = gameIdSelector(getState())

  dispatch(
    apiCallBegan({
      url: `/games/${id}/marketing_boards`,
      onStart: marketingBoardRequested.type,
      onSuccess: marketingBoardReceived.type,
      onError: marketingBoardRequestFailed.type
    })
  )
}

export const selectMarketingBoard = (id) => (dispatch) => dispatch(marketingBoardSelectMarket(id))

export const selectProductPoints = (productType, points) => (dispatch) => {
  dispatch(marketingBoardSelectProductPoints({ productType, points }))
}

export const payCosts = () => (dispatch, getState) => {
  const currentState = getState()
  const id = gameIdSelector(currentState)
  const { data } = currentState.marketingBoard

  const marketingBoards = data.map((market) => ({
    marketing_board_id: market.id,
    products: {
      h_basic_communication_points: market.h_basic_communication_points,
      h_plus_communication_points: market.h_plus_communication_points,
      h_ultra_communication_points: market.h_ultra_communication_points,
      next_gen_communication_points: market.next_gen_communication_points
    }
  }))

  dispatch(
    apiCallBegan({
      url: `/games/${id}/marketing_boards/pay_costs`,
      method: 'POST',
      data: { points_to_assign: marketingBoards },
      onStart: marketingBoardPayCostsRequested.type,
      onSuccess: marketingBoardPayCostsReceived.type,
      onError: marketingBoardPayCostsRequestFailed.type
    })
  )
}

export const setShowAlertMarketingBoard = (value) => (dispatch) =>
  dispatch(showAlertMarketingBoard(value))

export const setDescriptionToAlert = (value) => (dispatch) =>
  dispatch(descriptionToAlertReceived(value))

export const checkMarkets = () => (dispatch, getState) => {
  const currentState = getState()
  const { data } = currentState.marketingBoard
  const { items } = currentState.marketStrategy

  const { showAlert, description } = verificationMarketing(data, items)

  if (showAlert) {
    dispatch(setShowAlertMarketingBoard(true))
    dispatch(setDescriptionToAlert(description))
  } else {
    dispatch(payCosts())
  }
}
